import { GameEvent, GameState, Player, TurnEndGameEvent } from '../../../types/game';
import { finalizeTrailingSegment } from '../../../domain/segmentsCapture';
import { computeTurnAdvance, updatePlayerPositions } from '../../reducerHelpers';
import { applyTurnStats } from '../../../domain/stats';
import { WIN_SCORE } from '../../../utils/constants';
import { makeScoreEvent } from '../../../domain/events';
import { makeEmptyDiceMask } from '../../../domain/gameRules';

interface HandleTurnEndParams {
  state: GameState;
  event: TurnEndGameEvent;
  perf?: { inc?: (label: string) => void };
}

export function handleTurnEnd({ state, event, perf }: HandleTurnEndParams): GameState {
  // Idempotent TURN_END: one TURN_END per (turn, playerId).
  // W jednej rundzie (`turn`) poprawnie występują TURN_END dla wielu graczy.
  const turnEndPlayerId = event.payload?.playerId || null;
  if ((state.events || []).some((e) =>
    e.type === 'TURN_END'
    && e.turn === event.turn
    && (e.payload as TurnEndGameEvent['payload'] | undefined)?.playerId === turnEndPlayerId
  )) {
    perf?.inc?.('guard.turnEnd.duplicate');
    return state;
  }
  // Gdy gra jest już zamknięta (np. SCORE=1000 dotarł przed TURN_END), nie przesuwamy już tury.
  if (state.mode === 'ONLINE' && state.isGameOver && state.winner) {
    perf?.inc?.('guard.turnEnd.afterGameOver');
    return {
      ...state,
      events: [...(state.events || []), event],
    };
  }
  const nextEventsBase = [...(state.events || []), event];
  let nextPlayers = state.players;
  const prevSegmentsCount = state.segments?.length || 0;
  const finalizedSegments = finalizeTrailingSegment(state);
  const segmentsAddedThisTurn = finalizedSegments.length > prevSegmentsCount;
  const prevActiveId = state.turnCycle?.activePlayerId || state.players[state.currentPlayerIndex]?.id || null;
  let winnerCandidate: Player | undefined;
  const payload = event.payload;
  const autoScoreCandidateId = payload.playerId;
  const basePoints = payload.basePoints || 0;
  const hasScoreForTurn = (state.events || []).some(
    (ev: GameEvent) =>
      ev.type === 'SCORE'
      && ev.turn === event.turn
      && ev.payload.playerId === autoScoreCandidateId
  );
  const hasHistoryScore = (state.history || []).some((h) => h.turn === event.turn && h.playerId === autoScoreCandidateId);
  let nextIndex = state.currentPlayerIndex;
  const currentTurnPlayerId = state.mode === 'ONLINE'
    ? state.turnCycle?.activePlayerId || prevActiveId
    : prevActiveId;
  const turnAdvance = computeTurnAdvance({ ...state, players: nextPlayers }, nextPlayers, currentTurnPlayerId);
  nextIndex = turnAdvance.nextPlayerId ? nextPlayers.findIndex((p) => p.id === turnAdvance.nextPlayerId) : nextIndex;
  let nextTurnNumber = turnAdvance.nextTurnNumber;

  // Auto-score only in offline/REAL mode to avoid double-score races in ONLINE (host/clients submit SCORE explicitly).
  // Also require no existing SCORE event and no history entry for this turn/player.
  if (state.mode === 'REAL' && autoScoreCandidateId && basePoints > 0 && !hasScoreForTurn && !hasHistoryScore) {
    const playerIdx = state.players.findIndex((p) => p.id === autoScoreCandidateId);
    if (playerIdx !== -1) {
      const prev = state.players[playerIdx];
      const resultingScore = prev.score + basePoints;
      // If resulting score would exceed WIN_SCORE, treat as lost turn (do not auto-score).
      if (resultingScore > WIN_SCORE) {
        return {
          ...state,
          events: nextEventsBase,
          currentPlayerIndex: state.currentPlayerIndex,
          turnNumber: state.turnNumber,
        };
      }
      let updated = applyTurnStats(prev, {
        previousScore: prev.score,
        newScore: resultingScore,
        baseDelta: basePoints,
        isPass: resultingScore <= prev.score,
      });
      updated = {
        ...updated,
        score: resultingScore,
        hasFirstEntry: true,
        isWinner: updated.isWinner || resultingScore >= WIN_SCORE,
      };
      const autoScoreEvent = makeScoreEvent(event.turn, autoScoreCandidateId, basePoints, resultingScore);
      const playersCopy = [...state.players];
      playersCopy[playerIdx] = updated;
      nextPlayers = updatePlayerPositions(playersCopy);
      const finalIndex = nextPlayers.findIndex((p) => p.id === autoScoreCandidateId);
      nextIndex = updated.isWinner ? finalIndex : (finalIndex + 1) % nextPlayers.length;
      nextTurnNumber = updated.isWinner ? state.turnNumber : nextTurnNumber;
      nextEventsBase.push(autoScoreEvent);
      if (updated.isWinner) {
        const winIdx = nextPlayers.findIndex((p) => p.id === updated.id);
        if (winIdx !== -1) winnerCandidate = nextPlayers[winIdx];
      }
    }
  }

  let newState: GameState;
  if (state.mode === 'ONLINE' && state.turnCycle) {
    const nextActive = turnAdvance?.nextPlayerId || state.players[nextIndex]?.id || state.turnCycle.activePlayerId || null;
    const nextTurnCycle = nextActive
      ? {
          activePlayerId: nextActive,
          rollCount: 0,
          dice: [],
          kept: makeEmptyDiceMask(),
          capturedMask: makeEmptyDiceMask(),
          status: 'IDLE' as const,
          lastRoll: undefined,
          diceOrigins: [],
        }
      : undefined;
    newState = {
      ...state,
      segments: finalizedSegments,
      segmentsOwnerId: state.turnCycle?.activePlayerId || state.segmentsOwnerId,
      players: nextPlayers,
      currentPlayerIndex: nextIndex,
      turnNumber: nextTurnNumber,
      isGameOver: state.isGameOver || Boolean(winnerCandidate),
      winner: winnerCandidate || state.winner,
      events: nextEventsBase,
      turnCycle: nextTurnCycle,
    };
  } else {
    newState = {
      ...state,
      segments: finalizedSegments,
      segmentsOwnerId: state.turnCycle?.activePlayerId || state.segmentsOwnerId,
      players: nextPlayers,
      currentPlayerIndex: nextIndex,
      turnNumber: nextTurnNumber,
      isGameOver: state.isGameOver || Boolean(winnerCandidate),
      winner: winnerCandidate || state.winner,
      events: nextEventsBase,
    };
  }

  if (nextIndex < 0) {
    nextIndex = state.currentPlayerIndex;
    newState = { ...newState, currentPlayerIndex: nextIndex };
  }

  const turnAdvanced = nextTurnNumber !== state.turnNumber || nextIndex !== state.currentPlayerIndex;
  if (turnAdvanced) {
    const clearedNotices = (state.notices || []).filter((n) => {
      if (!n) return false;
      if (!n.playerId) return false; // global notice: czyścimy po zmianie tury
      if (prevActiveId && n.playerId === prevActiveId) return false;
      return true;
    });
    newState = {
      ...newState,
      notices: clearedNotices,
      ...(segmentsAddedThisTurn ? {} : { segments: [], segmentsOwnerId: undefined }),
      turnCycle: newState.turnCycle
        ? { ...newState.turnCycle, kept: makeEmptyDiceMask(), capturedMask: makeEmptyDiceMask() }
        : newState.turnCycle,
    };
  }

  return newState;
}
